import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { focusDone } from '../redux/actionCreators';
import { getIsFocusRequested } from '../redux/selectors';
import { useEditorDraftState } from './useEditorDraftState';
import { getRequiresFallback } from '../../DaEditorWrapper/redux/selectors';
/**
 * A Comment specific callback that we pass to `DaEditor's` `onEditorReady` prop.
 */ export function useCommentDaEditorReadyHandler(editorId, autoFocus) {
    const dispatch = useDispatch();
    const isFocusRequested = useSelector(partialRight(getIsFocusRequested, editorId, autoFocus));
    const shouldUseFallback = useSelector(getRequiresFallback);
    const draftState = useEditorDraftState(editorId);
    const hasDraft = useMemo(()=>{
        return !!draftState;
    }, [
        draftState
    ]);
    return useCallback((toolbars, api)=>{
        // We don't want to auto-focus drafts because they are triggered on pageload, this
        // avoids users getting lost when keyboard is bound to something potentially off-screen
        if (hasDraft && !isFocusRequested) {
            return;
        }
        /**
       * The actual editor is lazy loaded, so this `handleEditorReady` function is only called after a user clicks on the
       * loader's placeholder and the load finishes. That's also why we check `isFocusRequested` before dispatching `focusDone`.
       *
       *
       * Caveat:
       * Our fallback editor isn't lazy loaded. It loads with the page and we need to avoid this ready handler being
       * called unless focus is really requested. If we don't handle this case, focus will jump to the fallback editor
       * every page it shows up on regardless of if we requested it.
       */ // TODO: Fix this odd "focus-on-lazy-load" behaviour, it feels unintuitive. e.g. Make isFocusRequested true when we trigger the lazy load?
        if (isFocusRequested) {
            dispatch(focusDone(editorId));
        }
        if (!shouldUseFallback || shouldUseFallback && isFocusRequested) {
            // auto focus the editor because we always use lazyload and that will steal the click
            api.focusEditor();
        }
    }, [
        isFocusRequested,
        dispatch,
        editorId,
        hasDraft
    ]);
}

//# sourceMappingURL=useCommentDaEditorReadyHandler.js.map